import { computed, onBeforeUnmount, ref, unref } from 'vue';

const AXIS_LOCK_THRESHOLD = 4;
const VERTICAL_CANCEL_RATIO = 3;
const TAP_MAX_DISTANCE = 10;
const SWIPE_COMMIT_DISTANCE = 14;
const FLICK_VELOCITY_THRESHOLD = 0.12;
const EDGE_DRAG_RESISTANCE = 0.6;
const DRAG_OFFSET_VAR = '--carousel-drag-offset';

/**
 * Pointer drag / touch swipe for horizontal carousels.
 * Drag offset is applied via a CSS variable on `[data-carousel-track]` (rAF-batched)
 * so pointermove does not trigger Vue re-renders every frame.
 */
export function useCarouselSwipe({
  onNext,
  onPrev,
  enabled = () => true,
  canGoNext = () => true,
  canGoPrev = () => true,
}) {
  const viewportRef = ref(null);
  const isSwipeDragging = ref(false);

  let startX = 0;
  let startY = 0;
  let pointerId = null;
  let lockedAxis = null;
  let lastMoveX = 0;
  let lastMoveTime = 0;
  let velocityX = 0;
  let peakVelocityX = 0;
  let suppressNextClick = false;
  let documentSelectionLocked = false;
  let documentListenersAttached = false;
  let dragRafId = null;
  let pendingDragPx = 0;

  function getTrackEl() {
    return viewportRef.value?.querySelector('[data-carousel-track]') ?? null;
  }

  function flushDragOffset(px) {
    const track = getTrackEl();
    if (!track) return;
    track.style.setProperty(DRAG_OFFSET_VAR, `${px}px`);
  }

  function applyDragOffset(px) {
    pendingDragPx = px;
    if (dragRafId != null) return;

    dragRafId = requestAnimationFrame(() => {
      dragRafId = null;
      flushDragOffset(pendingDragPx);
    });
  }

  function clearDragOffset() {
    if (dragRafId != null) {
      cancelAnimationFrame(dragRafId);
      dragRafId = null;
    }
    pendingDragPx = 0;
    flushDragOffset(0);
  }

  function lockDocumentSelection() {
    if (documentSelectionLocked || typeof document === 'undefined') return;
    documentSelectionLocked = true;
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
  }

  function unlockDocumentSelection() {
    if (!documentSelectionLocked || typeof document === 'undefined') return;
    documentSelectionLocked = false;
    document.body.style.userSelect = '';
    document.body.style.webkitUserSelect = '';
  }

  function removeDocumentListeners() {
    if (!documentListenersAttached || typeof document === 'undefined') return;
    documentListenersAttached = false;

    document.removeEventListener('pointermove', onDocumentPointerMove, true);
    document.removeEventListener('pointerup', onDocumentPointerEnd, true);
    document.removeEventListener('pointercancel', onDocumentPointerEnd, true);
    window.removeEventListener('blur', forceResetDrag);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  }

  function addDocumentListeners() {
    if (documentListenersAttached || typeof document === 'undefined') return;
    documentListenersAttached = true;

    document.addEventListener('pointermove', onDocumentPointerMove, true);
    document.addEventListener('pointerup', onDocumentPointerEnd, true);
    document.addEventListener('pointercancel', onDocumentPointerEnd, true);
    window.addEventListener('blur', forceResetDrag);
    document.addEventListener('visibilitychange', onVisibilityChange);
  }

  function forceResetDrag() {
    isSwipeDragging.value = false;
    lockedAxis = null;
    pointerId = null;
    clearDragOffset();
    unlockDocumentSelection();
    removeDocumentListeners();
  }

  function clampDragOffset(dx) {
    if (dx > 0 && !unref(canGoPrev)) return dx * EDGE_DRAG_RESISTANCE;
    if (dx < 0 && !unref(canGoNext)) return dx * EDGE_DRAG_RESISTANCE;
    return dx;
  }

  function isTapGesture(dx) {
    return Math.abs(dx) <= TAP_MAX_DISTANCE;
  }

  function wasHorizontalDrag(dx) {
    return lockedAxis === 'x' && !isTapGesture(dx);
  }

  function shouldCommitSwipe(dx) {
    const absDx = Math.abs(dx);
    const flickVelocity = Math.max(Math.abs(velocityX), peakVelocityX);

    if (absDx >= SWIPE_COMMIT_DISTANCE) {
      return dx < 0 ? 'next' : 'prev';
    }

    if (flickVelocity >= FLICK_VELOCITY_THRESHOLD && absDx > TAP_MAX_DISTANCE) {
      if (velocityX !== 0) return velocityX < 0 ? 'next' : 'prev';
      return dx < 0 ? 'next' : 'prev';
    }

    return null;
  }

  function lockHorizontalAxis(dx, dy) {
    if (Math.abs(dx) < AXIS_LOCK_THRESHOLD && Math.abs(dy) < AXIS_LOCK_THRESHOLD) return false;

    const clearlyVertical = Math.abs(dy) > Math.abs(dx) * VERTICAL_CANCEL_RATIO
      && Math.abs(dy) >= AXIS_LOCK_THRESHOLD * 2;

    if (clearlyVertical) {
      forceResetDrag();
      return false;
    }

    if (Math.abs(dx) >= AXIS_LOCK_THRESHOLD || Math.abs(dx) >= Math.abs(dy)) {
      lockedAxis = 'x';
      return true;
    }

    return false;
  }

  function handlePointerMove(e) {
    if (pointerId == null || e.pointerId !== pointerId) return;

    const now = performance.now();
    const dt = now - lastMoveTime;
    if (dt > 0) {
      velocityX = (e.clientX - lastMoveX) / dt;
      peakVelocityX = Math.max(peakVelocityX, Math.abs(velocityX));
    }
    lastMoveX = e.clientX;
    lastMoveTime = now;

    const dx = e.clientX - startX;
    const dy = e.clientY - startY;

    if (!lockedAxis) {
      if (!lockHorizontalAxis(dx, dy)) return;
    }

    if (lockedAxis === 'x') {
      e.preventDefault();
      if (!isSwipeDragging.value) isSwipeDragging.value = true;
      lockDocumentSelection();
      applyDragOffset(clampDragOffset(dx));
    }
  }

  function finishDrag(e) {
    if (pointerId == null) return;
    if (e != null && e.pointerId !== pointerId) return;

    const dx = e ? e.clientX - startX : 0;
    const direction = wasHorizontalDrag(dx) ? shouldCommitSwipe(dx) : null;

    if (wasHorizontalDrag(dx)) {
      suppressNextClick = true;
      e?.preventDefault?.();
      e?.stopPropagation?.();
      flushDragOffset(clampDragOffset(dx));
    }

    pointerId = null;
    lockedAxis = null;
    removeDocumentListeners();
    unlockDocumentSelection();

    requestAnimationFrame(() => {
      isSwipeDragging.value = false;

      requestAnimationFrame(() => {
        if (direction === 'next' && unref(canGoNext)) onNext?.();
        else if (direction === 'prev' && unref(canGoPrev)) onPrev?.();
        clearDragOffset();
      });
    });
  }

  function onPointerDown(e) {
    if (!unref(enabled)) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (!viewportRef.value?.contains(e.target)) return;

    if (pointerId != null) {
      forceResetDrag();
    }

    suppressNextClick = false;
    pointerId = e.pointerId;
    startX = e.clientX;
    startY = e.clientY;
    lastMoveX = e.clientX;
    lastMoveTime = performance.now();
    velocityX = 0;
    peakVelocityX = 0;
    lockedAxis = null;
    isSwipeDragging.value = false;
    clearDragOffset();

    addDocumentListeners();
  }

  function onDocumentPointerMove(e) {
    handlePointerMove(e);
  }

  function onDocumentPointerEnd(e) {
    finishDrag(e);
  }

  function onVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      forceResetDrag();
    }
  }

  function onClickCapture(e) {
    if (!suppressNextClick) return;
    if (!viewportRef.value?.contains(e.target)) return;

    e.preventDefault();
    e.stopPropagation();
    suppressNextClick = false;
  }

  function onDragStart(e) {
    if (!unref(enabled)) return;
    e.preventDefault();
  }

  onBeforeUnmount(() => {
    forceResetDrag();
    suppressNextClick = false;
  });

  const viewportClass = computed(() => ({
    'cursor-grabbing': isSwipeDragging.value,
  }));

  const trackClass = computed(() => ({
    'transform-gpu': true,
    'backface-hidden': true,
    'will-change-transform': isSwipeDragging.value,
  }));

  const trackTransitionClass = computed(() => (
    isSwipeDragging.value
      ? ''
      : 'transition-[transform] duration-[260ms] ease-[cubic-bezier(0.25,0.46,0.45,0.94)]'
  ));

  return {
    viewportRef,
    isDragging: isSwipeDragging,
    viewportClass,
    trackClass,
    trackTransitionClass,
    swipeHandlers: {
      onPointerdown: onPointerDown,
      onClickCapture,
      onDragstart: onDragStart,
    },
  };
}
